import React, { useEffect, useRef, useState } from "react";
import { Sparkles, X, Send, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";

function sid() {
  let s = localStorage.getItem("lc_assistant_sid");
  if (!s) { s = `a-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`; localStorage.setItem("lc_assistant_sid", s); }
  return s;
}

export function AiAssistant() {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ role: "assistant", content: t("assistantIntro") }]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  const send = async () => {
    const message = text.trim();
    if (!message || busy) return;
    setText("");
    setMsgs((m) => [...m, { role: "user", content: message }]);
    setBusy(true);
    try {
      const { data } = await api.post("/assistant/chat", { session_id: sid(), message, lang });
      setMsgs((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMsgs((m) => [...m, { role: "assistant", content: "…" }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="assistant-open-btn"
          className="fixed bottom-5 right-5 z-[950] flex items-center gap-2 rounded-full bg-gold px-4 py-3 font-bold text-ink shadow-2xl gold-glow">
          <Sparkles size={18} /> <span className="hidden sm:inline">{t("assistantOpen")}</span>
        </button>
      )}
      {open && (
        <div data-testid="assistant-panel" className="fixed bottom-5 right-5 z-[950] flex h-[30rem] w-[min(92vw,22rem)] flex-col overflow-hidden rounded-3xl border border-cardline bg-card2/95 shadow-2xl backdrop-blur-xl">
          <div className="flex items-center justify-between border-b border-cardline bg-gold px-4 py-3 text-ink">
            <div className="flex items-center gap-2 font-heading font-bold"><Sparkles size={17} /> {t("assistantTitle")}</div>
            <button onClick={() => setOpen(false)} data-testid="assistant-close-btn" className="grid h-7 w-7 place-items-center rounded-full bg-ink/10"><X size={16} /></button>
          </div>
          <div className="flex-1 space-y-2.5 overflow-y-auto p-3" data-testid="assistant-messages">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2 text-sm ${m.role === "user" ? "bg-gold text-ink" : "border border-cardline bg-ink2/70 text-slate-100"}`}>{m.content}</div>
              </div>
            ))}
            {busy && <div className="flex justify-start"><div className="rounded-2xl border border-cardline bg-ink2/70 px-3.5 py-2 text-slate-300"><Loader2 size={16} className="animate-spin" /></div></div>}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 border-t border-cardline p-2.5">
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder={t("assistantPlaceholder")} data-testid="assistant-input"
              className="flex-1 rounded-full border border-cardline bg-ink2/60 px-4 py-2.5 text-sm text-white outline-none focus:border-gold" />
            <button type="submit" disabled={!text.trim() || busy} data-testid="assistant-send-btn" className="grid h-10 w-10 place-items-center rounded-full bg-gold text-ink disabled:opacity-50"><Send size={17} /></button>
          </form>
        </div>
      )}
    </>
  );
}
